import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Search, Grid3x3, List, Trash2, AlertTriangle, 
  CheckSquare, Square, Filter, X, Calculator, Plus, Users, ExternalLink, FileEdit,
  Clock, Send, FileCheck, Columns, Star, DollarSign, Scale
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import ManualClipEntry from '@/components/ManualClipEntry';
import HelpIcon from '@/components/HelpIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import ClipCard from '@/components/ClipCard';
import { MaterialTagForm } from '@/components/MaterialTagForm';
import { AssemblyBulkActions } from '@/components/AssemblyBulkActions';
import '@/styles/site-office.css';
import { PagePurpose } from '@/components/UXGuide';
import { ClipperAbsenceNotice } from '@/components/clipper/ClipperState';
import { Wordmark } from '@/components/brand/Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClipsLibrary() {
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  // DP276-M2 — the material being tagged from its own card.
  const [tagClip, setTagClip] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState('all');
  const [selectedPreferenceLevel, setSelectedPreferenceLevel] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [groupByPreference, setGroupByPreference] = useState(false);
  
  // Comparison mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareClips, setCompareClips] = useState([]);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  
  // Bulk preference editing
  const [showBulkPreferenceDialog, setShowBulkPreferenceDialog] = useState(false);
  const [bulkPreferenceLevel, setBulkPreferenceLevel] = useState('');
  
  // Selection for bulk operations
  const [selectedClips, setSelectedClips] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [showForceDeleteDialog, setShowForceDeleteDialog] = useState(false);
  
  // Assign to Portal state
  const [showPortalAssignDialog, setShowPortalAssignDialog] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  
  // Add to Estimate state
  const [showAddToEstimateDialog, setShowAddToEstimateDialog] = useState(false);
  const [selectedClipForEstimate, setSelectedClipForEstimate] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimateId, setSelectedEstimateId] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [estimateItems, setEstimateItems] = useState([]);
  
  // Save to Library state
  const [showSaveToLibraryDialog, setShowSaveToLibraryDialog] = useState(false);
  const [clipToSave, setClipToSave] = useState(null);
  const [libraryVisibility, setLibraryVisibility] = useState('personal');
  const [isShortcut, setIsShortcut] = useState(true);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  
  // Manual Entry state
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  
  // Submit for Approval state
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitDueDate, setSubmitDueDate] = useState('');
  const [submitPriority, setSubmitPriority] = useState('normal');
  const [submitNotes, setSubmitNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedProject, selectedType, selectedTag, selectedApprovalStatus, selectedPreferenceLevel, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedProject !== 'all') params.project_id = selectedProject;
      if (selectedType !== 'all') params.product_type = selectedType;
      if (selectedTag !== 'all') params.tag = selectedTag;
      if (selectedApprovalStatus !== 'all') params.approval_status = selectedApprovalStatus;
      if (selectedPreferenceLevel !== 'all') params.preference_level = selectedPreferenceLevel;
      if (searchQuery) params.search = searchQuery;

      const [clipsRes, projectsRes, tagsRes, typesRes] = await Promise.all([
        axios.get(`${API}/clips`, { params }),
        axios.get(`${API}/projects`),
        axios.get(`${API}/tags`),
        axios.get(`${API}/product-types`),
      ]);

      setClips(clipsRes.data);
      setProjects(projectsRes.data);
      setTags(tagsRes.data.tags);
      setProductTypes(typesRes.data.product_types);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load clips');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      setSelectedClips([]);
    }
  };

  const toggleClipSelection = (clipId) => {
    setSelectedClips(prev =>
      prev.includes(clipId) ? prev.filter(id => id !== clipId) : [...prev, clipId]
    );
  };

  const selectAll = () => {
    setSelectedClips(clips.map(c => c.id));
  };

  const deselectAll = () => {
    setSelectedClips([]);
  };

  const handleBulkDelete = async () => {
    if (selectedClips.length === 0) {
      toast.error('No clips selected');
      return;
    }

    try {
      const response = await axios.post(`${API}/clips/bulk-delete`, selectedClips);
      setDeleteResult(response.data);
      
      if (response.data.protected_count > 0) {
        // Show dialog with protected clips info
        setShowDeleteDialog(true);
      } else {
        toast.success(`Deleted ${response.data.deleted_count} clips`);
        setSelectedClips([]);
        setSelectMode(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting clips:', error);
      toast.error('Failed to delete clips');
    }
  };

  const handleForceDelete = async () => {
    try {
      const protectedIds = deleteResult?.protected_clips?.map(c => c.id) || [];
      const response = await axios.post(`${API}/clips/bulk-delete-force`, protectedIds);
      toast.success(`Force deleted ${response.data.deleted_count} clips`);
      setShowForceDeleteDialog(false);
      setShowDeleteDialog(false);
      setDeleteResult(null);
      setSelectedClips([]);
      setSelectMode(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to force delete clips');
    }
  };

  // Fetch contractors for portal assignment
  const fetchContractors = async () => {
    try {
      const response = await axios.get(`${API}/contractors`);
      setContractors(response.data);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  };

  // Open assign to portal dialog
  const openAssignToPortal = async () => {
    if (selectedClips.length === 0) {
      toast.error('No clips selected');
      return;
    }
    await fetchContractors();
    setSelectedContractors([]);
    setShowPortalAssignDialog(true);
  };

  // Toggle contractor selection
  const toggleContractorSelection = (contractorId) => {
    setSelectedContractors(prev =>
      prev.includes(contractorId) 
        ? prev.filter(id => id !== contractorId) 
        : [...prev, contractorId]
    );
  };

  // Assign clips to contractor portals
  const handleAssignToPortal = async () => {
    if (selectedContractors.length === 0) {
      toast.error('Please select at least one contractor');
      return;
    }

    setAssignLoading(true);
    try {
      // Assign to each selected contractor
      for (const contractorId of selectedContractors) {
        await axios.post(`${API}/contractor-portal/assign-clips`, {
          contractor_id: contractorId,
          clip_ids: selectedClips
        });
      }
      
      const clipCount = selectedClips.length;
      const contractorCount = selectedContractors.length;
      toast.success(`Assigned ${clipCount} spec sheet${clipCount > 1 ? 's' : ''} to ${contractorCount} contractor${contractorCount > 1 ? 's' : ''}`);
      
      setShowPortalAssignDialog(false);
      setSelectedContractors([]);
      setSelectedClips([]);
      setSelectMode(false);
    } catch (error) {
      console.error('Error assigning to portal:', error);
      toast.error('Failed to assign to contractor portal');
    } finally {
      setAssignLoading(false);
    }
  };

  // Save clip to Library
  const handleSaveToLibrary = async () => {
    if (!clipToSave) return;
    
    setSavingToLibrary(true);
    try {
      const libraryItem = {
        title: clipToSave.name || clipToSave.title || 'Untitled Clip',
        content: clipToSave.description || '',
        content_type: 'clip',
        visibility: libraryVisibility,
        icon: '🎬',
        cover_image: clipToSave.image_url || clipToSave.thumbnail_url,
        source_type: 'clip',
        source_id: clipToSave.id,
        is_shortcut: isShortcut,
        tags: clipToSave.tags || [],
        properties: {
          original_clip: clipToSave,
          product_type: clipToSave.product_type,
          manufacturer: clipToSave.manufacturer,
          model: clipToSave.model
        }
      };
      
      await axios.post(`${API}/knowledge/items`, libraryItem, {
        params: { user_id: 'user_001' }
      });
      
      toast.success(`Saved "${libraryItem.title}" to ${libraryVisibility === 'company' ? 'Studio' : 'My'} Library${isShortcut ? ' (Shortcut)' : ''}`);
      setShowSaveToLibraryDialog(false);
      setClipToSave(null);
    } catch (error) {
      console.error('Error saving to library:', error);
      toast.error('Failed to save to library');
    } finally {
      setSavingToLibrary(false);
    }
  };

  // Open Save to Library dialog
  const openSaveToLibrary = (clip) => {
    setClipToSave(clip);
    setLibraryVisibility('personal');
    setIsShortcut(true);
    setShowSaveToLibraryDialog(true);
  };

  // Fetch estimates for a project
  const fetchEstimatesForProject = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/estimates`);
      setEstimates(response.data.estimates || []);
    } catch (error) {
      console.error('Error fetching estimates:', error);
      setEstimates([]);
    }
  };

  // Fetch estimate items (locations/categories) for hierarchy placement
  const fetchEstimateItems = async (estimateId) => {
    try {
      const response = await axios.get(`${API}/estimates/${estimateId}`);
      const items = response.data.items || [];
      // Filter to only location and category items for parent selection
      setEstimateItems(items.filter(i => i.item_type === 'location' || i.item_type === 'category'));
    } catch (error) {
      console.error('Error fetching estimate items:', error);
      setEstimateItems([]);
    }
  };

  // Open Add to Estimate dialog
  const openAddToEstimate = (clip) => {
    setSelectedClipForEstimate(clip);
    setSelectedEstimateId('');
    setSelectedParentId('');
    setEstimates([]);
    setEstimateItems([]);
    setShowAddToEstimateDialog(true);
    
    // If clip has a project, fetch estimates for that project
    if (clip.project_id) {
      fetchEstimatesForProject(clip.project_id);
    }
  };

  // Add clip to estimate
  const handleAddToEstimate = async () => {
    if (!selectedEstimateId || !selectedClipForEstimate) {
      toast.error('Please select an estimate');
      return;
    }

    try {
      const clip = selectedClipForEstimate;
      const parentId = selectedParentId === '__top_level__' ? null : (selectedParentId || null);
      const newItem = {
        id: crypto.randomUUID(),
        name: clip.product_name || 'Unnamed Product',
        description: clip.description || '',
        parent_id: parentId,
        item_type: 'item',
        quantity: 1,
        unit: clip.unit || 'Each',
        waste_percent: 0,
        costs: {
          materials: clip.price ? parseFloat(clip.price) : 0,
          labor: 0,
          labor_hours: 0,
          labor_rate: 0,
          subcontract: 0,
          equipment: 0,
          other: 0
        },
        product_id: clip.id,
        product_name: clip.product_name,
        vendor_name: clip.vendor || '',
        is_allowance: false,
        is_alternate: false
      };

      await axios.post(`${API}/estimates/${selectedEstimateId}/items`, newItem);
      toast.success(`Added "${clip.product_name}" to estimate`);
      setShowAddToEstimateDialog(false);
      setSelectedClipForEstimate(null);
    } catch (error) {
      console.error('Error adding to estimate:', error);
      toast.error('Failed to add to estimate');
    }
  };

  // Submit selected clips for client approval
  const handleSubmitForApproval = async () => {
    if (selectedClips.length === 0) {
      toast.error('No clips selected');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const clipId of selectedClips) {
        try {
          await axios.post(`${API}/clips/${clipId}/submit-for-approval`, {
            decision_due_date: submitDueDate || null,
            decision_priority: submitPriority,
            notes: submitNotes,
            submitted_by: 'Designer'
          });
          successCount++;
        } catch (err) {
          console.error(`Error submitting clip ${clipId}:`, err);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Submitted ${successCount} selection(s) for client approval`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to submit ${errorCount} selection(s)`);
      }

      setShowSubmitDialog(false);
      setSubmitDueDate('');
      setSubmitPriority('normal');
      setSubmitNotes('');
      setSelectedClips([]);
      setSelectMode(false);
      fetchData();
    } catch (error) {
      console.error('Error submitting for approval:', error);
      toast.error('Failed to submit selections');
    }
  };

  // Check if there are active filters
  const hasActiveFilters = searchQuery || selectedProject !== 'all' || selectedType !== 'all' || selectedTag !== 'all' || selectedApprovalStatus !== 'all' || selectedPreferenceLevel !== 'all';

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedProject('all');
    setSelectedType('all');
    setSelectedTag('all');
    setSelectedApprovalStatus('all');
    setSelectedPreferenceLevel('all');
  };
  
  // Toggle compare mode
  const toggleCompareMode = () => {
    if (compareMode) {
      setCompareMode(false);
      setCompareClips([]);
    } else {
      setCompareMode(true);
      setSelectMode(false);
      setSelectedClips([]);
    }
  };
  
  // Add/remove clip from comparison
  const toggleCompareClip = (clipId) => {
    if (compareClips.includes(clipId)) {
      setCompareClips(compareClips.filter(id => id !== clipId));
    } else if (compareClips.length < 4) {
      setCompareClips([...compareClips, clipId]);
    } else {
      toast.error('Maximum 4 clips can be compared at once');
    }
  };
  
  // Bulk update preference level
  const handleBulkPreferenceUpdate = async () => {
    if (!bulkPreferenceLevel || selectedClips.length === 0) return;
    
    try {
      let successCount = 0;
      for (const clipId of selectedClips) {
        await axios.patch(`${API}/clips/${clipId}`, {
          preference_level: bulkPreferenceLevel
        });
        successCount++;
      }
      toast.success(`Updated ${successCount} clips to "${bulkPreferenceLevel}"`);
      setShowBulkPreferenceDialog(false);
      setBulkPreferenceLevel('');
      setSelectedClips([]);
      setSelectMode(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update preference levels');
    }
  };
  
  // Group clips by preference level
  const getGroupedClips = () => {
    if (!groupByPreference) return null;
    
    const groups = {
      'First Choice': [],
      'Good': [],
      'Better': [],
      'Best': [],
      'Alternative': [],
      'Unassigned': []
    };
    
    // DP109 ruling 1 — this read `filteredClips`, which does not exist in this
    // file. The list it groups is `clips`, as `getCompareClips` reads two lines
    // further down. (Nothing calls this function today; see the report.)
    clips.forEach(clip => {
      const level = clip.preference_level || 'Unassigned';
      if (groups[level]) {
        groups[level].push(clip);
      } else {
        groups['Unassigned'].push(clip);
      }
    });
    
    return groups;
  };
  
  // Get clips for comparison dialog
  const getComparisonClips = () => {
    return clips.filter(c => compareClips.includes(c.id));
  };

  // PREFERENCE_LEVELS constant
  const PREFERENCE_LEVELS = ['First Choice', 'Good', 'Better', 'Best', 'Alternative'];

  return (
    <div className="min-h-screen bg-background" data-testid="clips-library">
      {/* DP29 — one dismissible line, once per device, where the clipper's
          absence is the reason this page is empty. Never a popup. */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-4">
        <ClipperAbsenceNotice where="clips-library" />
      </div>
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-sm mb-3 text-muted-foreground">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="hover:underline hover:text-foreground"
            >
              My Desk
            </button>
            <span>/</span>
            <span className="text-foreground">Clip Library</span>
          </nav>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                My Desk
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2 relative">
                <HelpIcon 
                  elementId="nav-clip-library"
                  position="inline"
                  size="md"
                />
                <div>
                  <h1 className="font-heading font-light text-2xl tracking-tight text-foreground">
                    Clip Library
                  </h1>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {clips.length} total clips • Engineered by <Wordmark />
                  </p>
                </div>
                <BlueprintIndicator sectionId="clips-library-overview" tooltip="Learn about Clip Library" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowManualEntryDialog(true)}
                className="gap-2"
              >
                <FileEdit className="w-4 h-4" strokeWidth={1.5} />
                Manual Entry
              </Button>
              <Button
                variant={selectMode ? 'default' : 'outline'}
                onClick={toggleSelectMode}
                className={selectMode ? 'bg-[#70170F] hover:bg-[#70170F]/90' : ''}
              >
                {selectMode ? <CheckSquare className="w-4 h-4 mr-2" strokeWidth={1.5} /> : <Square className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                {selectMode ? 'Done' : 'Select'}
              </Button>
              {selectMode && selectedClips.length > 0 && (
                <>
                  <Button variant="outline" onClick={() => setShowSubmitDialog(true)}>
                    <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Submit for Approval ({selectedClips.length})
                  </Button>
                  <Button variant="outline" onClick={openAssignToPortal}>
                    <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Assign to Portal ({selectedClips.length})
                  </Button>
                  <Button variant="destructive" onClick={handleBulkDelete}>
                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Delete ({selectedClips.length})
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="border-b border-border bg-card" data-testid="clips-filter">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filters</span>
            <BlueprintIndicator sectionId="clips-filters" tooltip="Learn about filtering clips" size="sm" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search clips..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_code ? `${project.project_code} - ` : ''}{project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Product Types</SelectItem>
                  {productTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger>
                  <SelectValue placeholder="All Tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {tags.map((tag) => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={selectedApprovalStatus} onValueChange={setSelectedApprovalStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Approval Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">⏳ Pending</SelectItem>
                  <SelectItem value="submitted">📤 Submitted</SelectItem>
                  <SelectItem value="approved">✅ Approved</SelectItem>
                  <SelectItem value="rejected">❌ Rejected</SelectItem>
                  <SelectItem value="overdue">🚨 Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Second row of filters */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-3">
            <div className="md:col-span-2">
              <Select value={selectedPreferenceLevel} onValueChange={setSelectedPreferenceLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Preference Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Preferences</SelectItem>
                  <SelectItem value="First Choice">⭐ First Choice</SelectItem>
                  <SelectItem value="Good">👍 Good</SelectItem>
                  <SelectItem value="Better">👍👍 Better</SelectItem>
                  <SelectItem value="Best">🏆 Best</SelectItem>
                  <SelectItem value="Alternative">🔄 Alternative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox 
                id="groupByPreference"
                checked={groupByPreference}
                onCheckedChange={setGroupByPreference}
              />
              <label htmlFor="groupByPreference" className="text-sm cursor-pointer">
                Group by Preference
              </label>
            </div>
            <div className="md:col-span-2">
              <Button
                variant={compareMode ? 'default' : 'outline'}
                size="sm"
                onClick={toggleCompareMode}
                className={compareMode ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <Columns className="w-4 h-4 mr-2" />
                {compareMode ? `Compare (${compareClips.length}/4)` : 'Compare'}
              </Button>
            </div>
            {compareMode && compareClips.length >= 2 && (
              <div className="md:col-span-2">
                <Button onClick={() => setShowCompareDialog(true)}>
                  View Comparison
                </Button>
              </div>
            )}
            <div className="md:col-span-4 flex items-center gap-2 justify-end">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {/* Selection controls */}
          {selectMode && (
            <div className="mt-4 flex items-center gap-4 p-3 bg-muted rounded">
              <span className="text-sm text-muted-foreground">
                {selectedClips.length} of {clips.length} selected
              </span>
              <Button variant="ghost" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>
              {selectedClips.length > 0 && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowBulkPreferenceDialog(true)}
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  >
                    <Star className="w-4 h-4 mr-1" />
                    Set Preference
                  </Button>
                  {/* AS-2a — the selection, carried into an Assembly. */}
                  <AssemblyBulkActions clipIds={selectedClips} onDone={deselectAll} />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {tagClip ? (
        <div className="mb-6" data-testid="clips-tag-form">
          <MaterialTagForm
            projectId={tagClip.project_id}
            source="clip"
            body={{ clip_id: tagClip.id }}
            title={tagClip.title || tagClip.product_name || ''}
            room={tagClip.room || ''}
            location={tagClip.room_location || ''}
            tradePrefix={tagClip.spec_category || ''}
            tagName={tagClip.tag_name || ''}
            tagNote={tagClip.tag_note || ''}
            imageCount={(tagClip.images || []).length}
            onClose={() => setTagClip(null)}
          />
        </div>
      ) : null}
      {/* Clips Grid */}
      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-8">
        {/* Page Purpose */}
        <div className="mb-6">
          <PagePurpose currentStep="clips" darkMode={false} />
        </div>
        
        {loading && clips.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-muted-foreground">Loading clips...</p>
          </div>
        ) : clips.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-20 h-20 border border-border mb-6">
              <Grid3x3 className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-2xl mb-2">No clips found</h2>
            <p className="text-muted-foreground mb-6">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Use the Chrome extension to start clipping products'}
            </p>
          </div>
        ) : (
          <div
            className={`${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6' : 'space-y-4'}`}
          >
            {clips.map((clip, index) => (
              <div key={clip.id} className="relative group" data-testid={index === 0 ? 'clip-card' : `clip-card-${clip.id}`}>
                {selectMode && (
                  <div
                    className={`absolute top-2 left-2 z-20 w-6 h-6 border-2 flex items-center justify-center cursor-pointer ${
                      selectedClips.includes(clip.id)
                        ? 'bg-accent border-accent text-white'
                        : 'bg-white border-border'
                    }`}
                    onClick={() => toggleClipSelection(clip.id)}
                    data-testid={`clip-select-${clip.id}`}
                  >
                    {selectedClips.includes(clip.id) && <CheckSquare className="w-4 h-4" />}
                  </div>
                )}
                {compareMode && (
                  <div
                    className={`absolute top-2 left-2 z-20 w-6 h-6 border-2 flex items-center justify-center cursor-pointer rounded ${
                      compareClips.includes(clip.id)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-blue-400 hover:bg-blue-50'
                    }`}
                    onClick={() => toggleCompareClip(clip.id)}
                  >
                    {compareClips.includes(clip.id) && <CheckSquare className="w-4 h-4" />}
                    {!compareClips.includes(clip.id) && <span className="text-xs text-blue-600">+</span>}
                  </div>
                )}
                {/* Add to Estimate Button - positioned above ClipCard */}
                {!selectMode && !compareMode && (
                  <div className="absolute top-2 left-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      className="bg-[#BC5D41] hover:bg-[#A34E36] text-white shadow-lg"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openAddToEstimate(clip);
                      }}
                    >
                      <Calculator className="w-3 h-3 mr-1" />
                      Estimate
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="shadow-lg"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openSaveToLibrary(clip);
                      }}
                    >
                      📚 Library
                    </Button>
                  </div>
                )}
                <div className="relative self-start">
                  <ClipCard
                    clip={clip}
                    viewMode={viewMode}
                    onUpdate={fetchData}
                    onTag={setTagClip}
                  />
                  {clip.project_id && (
                    <Badge 
                      variant="secondary" 
                      className="absolute bottom-2 left-2 text-xs z-10"
                    >
                      In Project
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete Result Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Some Clips Are Protected
            </DialogTitle>
            <DialogDescription>
              {deleteResult?.deleted_count || 0} clips were deleted successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm mb-4">
              <strong>{deleteResult?.protected_count || 0} clips</strong> are assigned to projects and were not deleted:
            </p>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {deleteResult?.protected_clips?.map(clip => (
                <div key={clip.id} className="p-2 bg-muted rounded text-sm">
                  {clip.title}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setDeleteResult(null);
              setSelectedClips([]);
              setSelectMode(false);
              fetchData();
            }}>
              Keep Protected Clips
            </Button>
            <Button variant="destructive" onClick={() => setShowForceDeleteDialog(true)}>
              Force Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Delete Confirmation */}
      <AlertDialog open={showForceDeleteDialog} onOpenChange={setShowForceDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete clips that are assigned to projects.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleForceDelete} className="bg-destructive text-destructive-foreground">
              Yes, Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign to Portal Dialog */}
      <Dialog open={showPortalAssignDialog} onOpenChange={setShowPortalAssignDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-[#70170F]" />
              Assign to Contractor Portal
            </DialogTitle>
            <DialogDescription>
              Assign {selectedClips.length} spec sheet{selectedClips.length > 1 ? 's' : ''} to contractor portals
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block">Select Contractors</Label>
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {contractors.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4 text-center">No contractors found</p>
              ) : (
                contractors.map(contractor => (
                  <div
                    key={contractor.id}
                    className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedContractors.includes(contractor.id) ? 'bg-[#70170F]/10' : ''
                    }`}
                    onClick={() => toggleContractorSelection(contractor.id)}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedContractors.includes(contractor.id) 
                        ? 'bg-[#70170F] border-[#70170F]' 
                        : 'border-gray-300'
                    }`}>
                      {selectedContractors.includes(contractor.id) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{contractor.company_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {contractor.contact_name} • {contractor.specialty || 'No specialty'}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {contractor.contractor_hierarchy === 'general_contractor' ? 'GC' : 
                       contractor.contractor_hierarchy === 'sub_subcontractor' ? 'Sub-sub' : 'Sub'}
                    </Badge>
                  </div>
                ))
              )}
            </div>
            {selectedContractors.length > 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                {selectedContractors.length} contractor{selectedContractors.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPortalAssignDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAssignToPortal}
              disabled={selectedContractors.length === 0 || assignLoading}
              className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            >
              {assignLoading ? 'Assigning...' : `Assign to ${selectedContractors.length || 0} Portal${selectedContractors.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Estimate Dialog */}
      <Dialog open={showAddToEstimateDialog} onOpenChange={setShowAddToEstimateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Estimate</DialogTitle>
            <DialogDescription>
              Add &quot;{selectedClipForEstimate?.product_name}&quot; to an estimate
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Project Selection */}
            <div>
              <Label>Project</Label>
              <Select
                value={selectedClipForEstimate?.project_id || ''}
                onValueChange={(value) => {
                  fetchEstimatesForProject(value);
                  setSelectedEstimateId('');
                  setSelectedParentId('');
                  setEstimateItems([]);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estimate Selection */}
            <div>
              <Label>Estimate</Label>
              <Select
                value={selectedEstimateId}
                onValueChange={(value) => {
                  setSelectedEstimateId(value);
                  fetchEstimateItems(value);
                  setSelectedParentId('');
                }}
                disabled={estimates.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={estimates.length === 0 ? "No estimates for this project" : "Select an estimate"} />
                </SelectTrigger>
                <SelectContent>
                  {estimates.map(est => (
                    <SelectItem key={est.id} value={est.id}>
                      {est.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {estimates.length === 0 && selectedClipForEstimate?.project_id && (
                <p className="text-xs text-muted-foreground mt-1">
                  Create an estimate first in the Project Hub
                </p>
              )}
            </div>

            {/* Location/Category Selection (Optional) */}
            {estimateItems.length > 0 && (
              <div>
                <Label>Add under (optional)</Label>
                <Select
                  value={selectedParentId}
                  onValueChange={setSelectedParentId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Top level (no parent)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__top_level__">Top level (no parent)</SelectItem>
                    {estimateItems.map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.item_type === 'location' ? '📍 ' : '📁 '}{item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Clip Preview */}
            {selectedClipForEstimate && (
              <div className="bg-muted p-3 rounded-lg">
                <p className="font-medium text-sm">{selectedClipForEstimate.product_name}</p>
                {selectedClipForEstimate.brand && (
                  <p className="text-xs text-muted-foreground">{selectedClipForEstimate.brand}</p>
                )}
                {selectedClipForEstimate.price && (
                  <p className="text-sm font-semibold text-[#BC5D41] mt-1">
                    ${parseFloat(selectedClipForEstimate.price).toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToEstimateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddToEstimate} disabled={!selectedEstimateId}>
              <Plus className="w-4 h-4 mr-2" />
              Add to Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save to Library Dialog */}
      <Dialog open={showSaveToLibraryDialog} onOpenChange={setShowSaveToLibraryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📚 Save to Library
            </DialogTitle>
            <DialogDescription>
              Save this clip to your centralized resource library
            </DialogDescription>
          </DialogHeader>
          
          {clipToSave && (
            <div className="space-y-4 py-2">
              {/* Preview */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                {clipToSave.image_url && (
                  <img 
                    src={clipToSave.image_url} 
                    alt="" 
                    className="w-16 h-16 object-cover rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{clipToSave.name || clipToSave.title}</p>
                  <p className="text-xs text-muted-foreground">{clipToSave.product_type}</p>
                </div>
              </div>
              
              {/* Library Selection */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Save to</Label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setLibraryVisibility('personal')}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      libraryVisibility === 'personal' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-xl block mb-1">👤</span>
                    <span className="text-xs font-medium">My Library</span>
                  </button>
                  <button
                    onClick={() => setLibraryVisibility('company')}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      libraryVisibility === 'company' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-xl block mb-1">🏢</span>
                    <span className="text-xs font-medium">Studio Library</span>
                  </button>
                  <button
                    onClick={() => setLibraryVisibility('admin')}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      libraryVisibility === 'admin' 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-xl block mb-1">🔒</span>
                    <span className="text-xs font-medium">Admin Library</span>
                  </button>
                </div>
              </div>
              
              {/* Shortcut Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Show as Shortcut</p>
                  <p className="text-xs text-muted-foreground">Quick access from sidebar</p>
                </div>
                <button
                  onClick={() => setIsShortcut(!isShortcut)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    isShortcut ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                    isShortcut ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveToLibraryDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveToLibrary} 
              disabled={savingToLibrary}
              className="bg-[#BC5D41] hover:bg-[#70170F]"
            >
              {savingToLibrary ? 'Saving...' : '📚 Save to Library'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Manual Clip Entry Dialog */}
      <ManualClipEntry
        isOpen={showManualEntryDialog}
        onClose={() => setShowManualEntryDialog(false)}
        projectId={selectedProject !== 'all' ? selectedProject : null}
        onClipCreated={() => {
          fetchData(); // Refresh clips list
        }}
      />

      {/* Submit for Approval Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#70170F]" />
              Submit for Client Approval
            </DialogTitle>
            <DialogDescription>
              Submit {selectedClips.length} selection{selectedClips.length > 1 ? 's' : ''} for client review in the Drawing Room
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm">Decision Due Date (optional)</Label>
              <Input
                type="date"
                value={submitDueDate}
                onChange={(e) => setSubmitDueDate(e.target.value)}
                className="mt-1"
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground mt-1">
                When should the client decide by?
              </p>
            </div>
            <div>
              <Label className="text-sm">Priority</Label>
              <Select value={submitPriority} onValueChange={setSubmitPriority}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">🟢 Low</SelectItem>
                  <SelectItem value="normal">🔵 Normal</SelectItem>
                  <SelectItem value="high">🟠 High</SelectItem>
                  <SelectItem value="urgent">🔴 Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Notes to Client (optional)</Label>
              <Input
                placeholder="Please review and approve by Friday..."
                value={submitNotes}
                onChange={(e) => setSubmitNotes(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitForApproval} className="bg-[#70170F] hover:bg-[#70170F]/90">
              <Send className="w-4 h-4 mr-2" />
              Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Comparison Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Clip Comparison
            </DialogTitle>
            <DialogDescription>
              Compare {compareClips.length} selections side by side
            </DialogDescription>
          </DialogHeader>
          
          <div className={`grid grid-cols-${Math.min(compareClips.length, 4)} gap-4`}>
            {getComparisonClips().map(clip => (
              <div key={clip.id} className="border rounded-lg p-4 space-y-3">
                {/* Image */}
                {clip.image && (
                  <img 
                    src={clip.image} 
                    alt={clip.title}
                    className="w-full h-40 object-cover rounded"
                  />
                )}
                
                {/* Title & Brand */}
                <div>
                  <h4 className="font-semibold text-lg">{clip.title}</h4>
                  {clip.brand && <p className="text-sm text-muted-foreground">{clip.brand}</p>}
                </div>
                
                {/* Preference Badge */}
                {clip.preference_level && (
                  <Badge variant="outline" className="text-xs">
                    {clip.preference_level === 'First Choice' && '⭐ '}
                    {clip.preference_level === 'Best' && '🏆 '}
                    {clip.preference_level}
                  </Badge>
                )}
                
                {/* Pricing */}
                <div className="bg-muted/50 p-3 rounded space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Base Cost:</span>
                    <span className="font-medium">${(clip.base_cost || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Client Price:</span>
                    <span className="font-medium text-green-600">${(clip.client_price || clip.price || 0).toLocaleString()}</span>
                  </div>
                  {clip.is_allowance && (
                    <div className="flex justify-between border-t pt-1 mt-1">
                      <span>Allowance:</span>
                      <span className="font-medium">${(clip.allowance_amount || 0).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                
                {/* Specs */}
                <div className="text-xs space-y-1 text-muted-foreground">
                  {clip.product_type && <div>Type: {clip.product_type}</div>}
                  {clip.room && <div>Room: {clip.room}</div>}
                  {clip.spec_category && clip.spec_num && (
                    <div>Spec: {clip.spec_num}</div>
                  )}
                </div>
                
                {/* Description */}
                {clip.description && (
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {clip.description}
                  </p>
                )}
                
                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/clips/${clip.id}`)}>
                    View Details
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => {
                      setCompareClips(compareClips.filter(id => id !== clip.id));
                      if (compareClips.length <= 2) setShowCompareDialog(false);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Preference Update Dialog */}
      <Dialog open={showBulkPreferenceDialog} onOpenChange={setShowBulkPreferenceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Set Preference Level
            </DialogTitle>
            <DialogDescription>
              Update preference level for {selectedClips.length} selected clips
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-sm font-medium">Preference Level</Label>
            <Select value={bulkPreferenceLevel} onValueChange={setBulkPreferenceLevel}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select preference level..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="First Choice">⭐ First Choice</SelectItem>
                <SelectItem value="Good">👍 Good</SelectItem>
                <SelectItem value="Better">👍👍 Better</SelectItem>
                <SelectItem value="Best">🏆 Best</SelectItem>
                <SelectItem value="Alternative">🔄 Alternative</SelectItem>
              </SelectContent>
            </Select>
            
            <p className="mt-4 text-sm text-muted-foreground">
              This helps organize selections for client presentation with Good/Better/Best options.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkPreferenceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkPreferenceUpdate}
              disabled={!bulkPreferenceLevel}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Update {selectedClips.length} Clips
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
